/**
 * Picture — one-stop responsive image component.
 *
 * Renders a <picture> with a WebP source + original-format fallback. Assumes
 * the WebP sibling lives at the same path with a .webp extension.
 *
 * Usage:
 *   <Picture
 *     src="/hero-bowls-therapy.webp"
 *     alt="…"
 *     width={1200} height={800}
 *     className="w-full h-[540px] object-cover"
 *     priority     // hero / above-the-fold
 *   />
 *
 * Off-screen images should omit `priority` so they get loading="lazy" and
 * don't compete with the hero for bandwidth. Every `<img>` keeps explicit
 * width/height so the pixel box is reserved before the bytes arrive.
 */
export default function Picture({
  src,
  alt,
  width,
  height,
  className = "",
  style,
  priority = false,
  objectPosition,
  testId,
}) {
  // Derive the WebP sibling path. When the caller already passes a .webp
  // src (e.g. the Feb 2026 image-compression pass moved every hero from
  // .jpg to .webp), the extra <source> is redundant — skip it so the DOM
  // stays tidy and the browser doesn't do a needless double-match.
  const dot = src.lastIndexOf(".");
  const ext = dot > 0 ? src.slice(dot + 1).toLowerCase() : "";
  const webpSrc = dot > 0 && ext !== "webp" ? `${src.slice(0, dot)}.webp` : null;
  const merged = objectPosition ? { ...style, objectPosition } : style;

  return (
    <picture>
      {webpSrc && <source srcSet={webpSrc} type="image/webp" />}
      <img
        src={src}
        alt={alt}
        width={width}
        height={height}
        loading={priority ? "eager" : "lazy"}
        fetchpriority={priority ? "high" : "auto"}
        decoding="async"
        className={className}
        style={merged}
        data-testid={testId}
      />
    </picture>
  );
}

export { Picture };
